import React, { useEffect, useState } from "react";
import {
    Card, Typography, Table, Input, Select, Button, Space, Tag,
    Row, Col, Empty, Progress,
} from "antd";
import {
    PlusOutlined, SearchOutlined, EyeOutlined, EditOutlined,
    DownOutlined, ArrowLeftOutlined, DownloadOutlined, FilterOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { MilestoneService } from "../../../Services/Manage/MilestoneService";
import { UserService } from "../../../Services/Manage/UserService";

const { Text } = Typography;

const STATUS_COLORS = {
    Planned: "#8c8c8c",
    "In Progress": "#fa8c16",
    Completed: "#52c41a",
    Pending: "#1677ff",
    Overdue: "#f5222d",
};

const PRIORITY_COLORS = {
    High: "#f5222d",
    Medium: "#fa8c16",
    Low: "#52c41a",
};

const getPermission = (link) => {
    try {
        const raw = localStorage.getItem("ome_permission");
        const parsed = JSON.parse(raw);
        const permissions = Array.isArray(parsed) ? parsed : JSON.parse(parsed);
        return permissions.find((p) => p.link === link) || { add: false, edit: false, delete: false, read: false };
    } catch {
        return { add: false, edit: false, delete: false, read: false };
    }
};

const MilestonesList = () => {
    const navigate = useNavigate();
    const location = useLocation();

const perm = getPermission("/overview-project");

    const [userMap, setUserMap] = useState({});

    useEffect(() => {
        (async () => {
            try {
                const res = await UserService.getAllUsers();
                const map = {};
                (res.data || []).forEach((u) => {
                    if (u.user_id) map[String(u.user_id)] = u.name;
                });
                setUserMap(map);
            } catch (error) {
                console.error(error);
            }
        })();
    }, []);

    const project = {
        id: location.state?.id || null,
        pro_name: location.state?.pro_name || "-",
        pro_no: location.state?.pro_no || "-",
        customer: location.state?.pro_customer || "-",
        managerId: location.state?.pro_manager || null,
        value: location.state?.pro_value || 0,
        engineerIds: location.state?.pro_engineer || [],
        departmentName: location.state?.pro_department_name || "-",
        start_date: (location.state?.pro_start_date && dayjs(location.state.pro_start_date).isValid())
            ? dayjs(location.state.pro_start_date).format("DD-MMM-YYYY")
            : "-",
        end_date: (location.state?.pro_target_end_date && dayjs(location.state.pro_target_end_date).isValid())
            ? dayjs(location.state.pro_target_end_date).format("DD-MMM-YYYY")
            : "-",
        status: location.state?.pro_status || "Not Started",
    };

const managerName = userMap[String(project.managerId)] || project.managerId || "-";
    const viewAll = perm.view_all === true;

    const [milestones, setMilestones] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState(null);
    const [priorityFilter, setPriorityFilter] = useState(null);
    const [selectedMilestone, setSelectedMilestone] = useState(null);
const [isProjectManager, setIsProjectManager] = useState(false);
    const [permissionsReady, setPermissionsReady] = useState(false);

    const canManage = viewAll || isProjectManager;
    useEffect(() => {
        fetchMilestones();
    }, []);

    const fetchMilestones = async () => {
        try {
            setLoading(true);
            const request = project.id
                ? await MilestoneService.getAllByProject(project.id)
                : await MilestoneService.getAllMilestone();

            if (request?.status) {
                let rawRows = request.data || [];

if (!viewAll) {
                    const user = await UserService.getOmeUser();
                    const uid = String(user.uid).trim();
                    const uname = (user.name || "").trim().toLowerCase();

                    const managerMatch = String(project.managerId ?? "").trim() === uid;
                    setIsProjectManager(managerMatch);

                    if (!managerMatch) {
                        rawRows = rawRows.filter((m) => {
                            const addedByMatch = String(m.added_by ?? "").trim() === uid;
                            const assigneeMatch = (m.assignee || "").trim().toLowerCase() === uname;
                            return addedByMatch || assigneeMatch;
                        });
                    }
                } else {
                    setIsProjectManager(true);
                }
                setPermissionsReady(true);

                const rows = rawRows.map((m) => ({
                    dbId: m.id,
                    code: m.milestone_code,
                    name: m.milestone_name,
                    description: m.description,
                    start_date: m.start_date ? dayjs(m.start_date).format("DD-MMM-YYYY") : "-",
                    end_date: m.end_date ? dayjs(m.end_date).format("DD-MMM-YYYY") : "-",
                    assignee: m.assignee,
                    status: m.status,
                    progress: m.progress || 0,
                    priority: m.priority,
                    deliverables: m.deliverables,
                }));
                setMilestones(rows);
                if (rows.length) setSelectedMilestone((prev) => prev || rows[0]);
            } else {
                setMilestones([]);
            }
        } catch (error) {
            console.error("Error fetching milestones:", error);
            setMilestones([]);
        } finally {
            setLoading(false);
        }
    };

    const filteredData = milestones.filter((m) => {
        const text = searchText.toLowerCase();
        const matchesSearch = !text || (m.name || "").toLowerCase().includes(text);
        const matchesStatus = !statusFilter || m.status === statusFilter;
        const matchesPriority = !priorityFilter || m.priority === priorityFilter;
        return matchesSearch && matchesStatus && matchesPriority;
    });

    const completedCount = milestones.filter((m) => m.status === "Completed").length;
    const inProgressCount = milestones.filter((m) => m.status === "In Progress").length;
    const pendingCount = milestones.filter((m) => m.status === "Pending").length;
    const overdueCount = milestones.filter((m) => m.status === "Overdue").length;
    const overallProgress = milestones.length
        ? Math.round(milestones.reduce((s, m) => s + (m.progress || 0), 0) / milestones.length)
        : 0;

    const goAddMilestone = () =>
        navigate("/milestones/add", {
            state: {
                id: project.id,
                pro_name: project.pro_name,
                pro_no: project.pro_no,
                pro_engineer: project.engineerIds,
                pro_department_name: project.departmentName,
            },
        });

const goViewEditMilestone = (record, editable) =>
        navigate("/milestones/add", {
            state: {
                id: project.id,
                pro_name: project.pro_name,
                pro_no: project.pro_no,
                milestoneDbId: record.dbId,
                action: editable ? "Edit" : "View",
                enableEdit: editable,
                statusOnly: !canManage,
                canManage,
                pro_engineer: project.engineerIds,
                pro_department_name: project.departmentName,
            },
        });

    const columns = [
        { title: "#", key: "sno", width: 50, align: "center", render: (_, __, i) => i + 1 },
        { title: "Milestone Name", dataIndex: "name", key: "name", render: (t) => <Text strong>{t}</Text> },
        { title: "Description", dataIndex: "description", key: "description", ellipsis: true },
        { title: "Start Date", dataIndex: "start_date", key: "start_date" },
        { title: "End Date", dataIndex: "end_date", key: "end_date" },
        { title: "Assignee", dataIndex: "assignee", key: "assignee" },
        {
            title: "Status", dataIndex: "status", key: "status",
            render: (v) => (
                <span style={{
                    background: `${STATUS_COLORS[v] || "#bfbfbf"}1a`, color: STATUS_COLORS[v] || "#595959",
                    padding: "3px 10px", borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>{v || "-"}</span>
            ),
        },
        {
            title: "Progress", dataIndex: "progress", key: "progress", width: 140,
            render: (v) => <Progress percent={v || 0} size="small" />,
        },
        {
            title: "Priority", dataIndex: "priority", key: "priority",
            render: (v) => v ? (
                <span style={{
                    background: `${PRIORITY_COLORS[v] || "#bfbfbf"}1a`, color: PRIORITY_COLORS[v] || "#595959",
                    padding: "3px 10px", borderRadius: 6, fontWeight: 600, fontSize: 12,
                }}>{v}</span>
            ) : "-",
        },
        {
            title: "Action", key: "action", align: "center",
            render: (_, record) => (
                <Space size="small">
                    <Button size="small" icon={<EyeOutlined />} disabled={!permissionsReady} onClick={(e) => { e.stopPropagation(); setSelectedMilestone(record); goViewEditMilestone(record, false); }} />
                    <Button size="small" icon={<EditOutlined />} disabled={!permissionsReady} onClick={(e) => { e.stopPropagation(); goViewEditMilestone(record, true); }} />
                </Space>
            ),
        },
    ];

    const deliverableList = (selectedMilestone?.deliverables || "")
        .split("\n")
        .map((l) => l.trim())
        .filter(Boolean);

    return (
        <Card bordered={false} bodyStyle={{ padding: 0 }}>
            {/* Header */}
            <div style={{ padding: "8px 16px", borderBottom: "1px solid #f0f0f0", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8 }}>
                <div>
                    <Space align="center" size={8}>
                        <ArrowLeftOutlined
                            style={{ cursor: "pointer", fontSize: "18px" }}
                            onClick={() => navigate("/overview-project/view", { state: { id: project.id } })}
                        />
                        <Typography.Title level={4} style={{ margin: 0, color: "#1658b4" }}>Milestones</Typography.Title>
                        <Tag color="green">{project.status}</Tag>
                    </Space>
                </div>
               <Space wrap>
                    <Button size="middle" onClick={() => navigate("/overview-project/view", { state: { id: project.id } })}>
                        View Project
                    </Button>
                    {canManage && (
                        <Button type="primary" icon={<PlusOutlined />} onClick={goAddMilestone}
                            style={{ backgroundColor: "rgba(14, 93, 146, 0.95)", borderColor: "rgba(14, 93, 146, 0.95)" }}>
                            Add Milestone
                        </Button>
                    )}
                    <Button size="middle" icon={<DownOutlined />}>More Actions</Button>
                </Space>
            </div>

            {/* Two separate cards side by side */}
            <div style={{ padding: "12px 16px 0" }}>
                <Row gutter={16}>
                    <Col xs={24} lg={14}>
                        <Card bodyStyle={{ padding: "14px 20px" }} style={{ height: "100%" }}>
                            <Space align="start" size={12}>
                                <div style={{ width: 40, height: 40, borderRadius: 8, background: "#f9f0ff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0 }}>📁</div>
                                <div>
                                    <div style={{ marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 15 }}>{project.pro_name}</Text>
                                        <Tag color="cyan" style={{ fontSize: 10, marginLeft: 10 }}>{project.pro_no}</Tag>
                                    </div>
                                    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 16 }}>
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Customer</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.customer}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Project Manager</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{managerName}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Start Date</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.start_date}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>End Date</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>{project.end_date}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Project Value</div>
                                            <div style={{ fontSize: 12.5, fontWeight: 600, color: "#333" }}>₹{Number(project.value).toLocaleString("en-IN")}</div>
                                        </div>
                                        <div style={{ width: 1, height: 28, background: "#e8e8e8" }} />
                                        <div>
                                            <div style={{ fontSize: 11, color: "#999" }}>Status</div>
                                            <Tag color="blue" style={{ fontSize: 11 }}>{project.status}</Tag>
                                        </div>
                                    </div>
                                </div>
                            </Space>
                        </Card>
                    </Col>

                    <Col xs={24} lg={10}>
                        <Card bodyStyle={{ padding: "14px 20px" }} style={{ height: "100%" }}>
                            <Row align="middle" gutter={[12, 8]}>
                                <Col xs={24} sm={8}>
                                    <div style={{ fontSize: 11, color: "#888" }}>Milestone Progress</div>
                                    <div style={{ fontSize: 26, fontWeight: 700, color: "#1677ff" }}>{overallProgress}%</div>
                                    <Progress percent={overallProgress} size="small" showInfo={false} />
                                </Col>
                                <Col xs={24} sm={16}>
                                    <Row gutter={[8, 6]}>
                                        <Col span={12} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#52c41a", display: "inline-block" }} />
                                            Completed <b>{completedCount}</b> ({milestones.length ? Math.round((completedCount / milestones.length) * 100) : 0}%)
                                        </Col>
                                        <Col span={12} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#fa8c16", display: "inline-block" }} />
                                            In Progress <b>{inProgressCount}</b> ({milestones.length ? Math.round((inProgressCount / milestones.length) * 100) : 0}%)
                                        </Col>
                                        <Col span={12} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#1677ff", display: "inline-block" }} />
                                            Pending <b>{pendingCount}</b> ({milestones.length ? Math.round((pendingCount / milestones.length) * 100) : 0}%)
                                        </Col>
                                        <Col span={12} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#f5222d", display: "inline-block" }} />
                                            Overdue <b>{overdueCount}</b> ({milestones.length ? Math.round((overdueCount / milestones.length) * 100) : 0}%)
                                        </Col>
                                    </Row>
                                    <Text type="secondary" style={{ fontSize: 11, display: "block", marginTop: 6 }}>Total Milestones: {milestones.length}</Text>
                                </Col>
                            </Row>
                        </Card>
                    </Col>
                </Row>
            </div>

            {/* Milestone list + right details panel */}
            <div style={{ padding: "12px 16px 16px" }}>
                <Row gutter={16} align="top">
                    <Col xs={24} lg={17}>
                        <Card
                            title={<Text strong style={{ fontSize: 16 }}>Milestone List</Text>}
                            bodyStyle={{ padding: 12 }}
                        >
                            <Card style={{ marginBottom: 12 }} bodyStyle={{ padding: 12 }}>
                                <Row gutter={[16, 16]}>
                                    <Col xs={24} sm={12} md={8}>
                                        <Input
                                            allowClear
                                            placeholder="Search milestone..."
                                            prefix={<SearchOutlined />}
                                            value={searchText}
                                            onChange={(e) => setSearchText(e.target.value)}
                                        />
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Select allowClear placeholder="All Status" style={{ width: "100%" }}
                                            value={statusFilter} onChange={setStatusFilter}
                                            options={Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s }))} />
                                    </Col>
                                    <Col xs={24} sm={12} md={5}>
                                        <Select allowClear placeholder="All Priority" style={{ width: "100%" }}
                                            value={priorityFilter} onChange={setPriorityFilter}
                                            options={Object.keys(PRIORITY_COLORS).map((s) => ({ value: s, label: s }))} />
                                    </Col>
                                    <Col xs={24} sm={12} md={6}>
                                        <Space>
                                            <Button icon={<FilterOutlined />}>More Filters</Button>
                                            <Button icon={<DownloadOutlined />} />
                                        </Space>
                                    </Col>
                                </Row>
                            </Card>

                            {milestones.length === 0 && !loading ? (
                                <Empty description="No milestones yet" />
                            ) : (
                                <Table
                                    rowKey="dbId"
                                    dataSource={filteredData}
                                    columns={columns}
                                    loading={loading}
                                    pagination={{ pageSize: 10 }}
                                    scroll={{ x: "max-content" }}
                                    onRow={(record) => ({ onClick: () => setSelectedMilestone(record) })}
                                />
                            )}
                        </Card>
                    </Col>

                    {/* Right: Milestone Details */}
                    <Col xs={24} lg={7}>
                        <Card
                            title={<Text strong style={{ fontSize: 15 }}>Milestone Details</Text>}
                            bodyStyle={{ padding: 16 }}
                        >
                            {!selectedMilestone ? (
                                <Empty description="Select a milestone" />
                            ) : (
                                <>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                                        <Text strong style={{ fontSize: 15 }}>{selectedMilestone.name}</Text>
                                        <span style={{
                                            background: `${STATUS_COLORS[selectedMilestone.status] || "#bfbfbf"}1a`,
                                            color: STATUS_COLORS[selectedMilestone.status] || "#595959",
                                            padding: "2px 10px", borderRadius: 6, fontWeight: 600, fontSize: 11,
                                        }}>{selectedMilestone.status || "-"}</span>
                                    </div>

                                    <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 12 }}>
                                        <Progress
                                            type="circle"
                                            percent={selectedMilestone.progress || 0}
                                            size={80}
                                            strokeColor="#fa8c16"
                                        />
                                        <div style={{ flex: 1 }}>
                                            <div style={{ fontSize: 12, marginBottom: 4 }}><Text type="secondary">Start Date</Text> <b style={{ float: "right" }}>{selectedMilestone.start_date}</b></div>
                                            <div style={{ fontSize: 12, marginBottom: 4 }}><Text type="secondary">End Date</Text> <b style={{ float: "right" }}>{selectedMilestone.end_date}</b></div>
                                            <div style={{ fontSize: 12, marginBottom: 4 }}><Text type="secondary">Assignee</Text> <b style={{ float: "right" }}>{selectedMilestone.assignee || "-"}</b></div>
                                            <div style={{ fontSize: 12 }}><Text type="secondary">Priority</Text> <b style={{ float: "right", color: PRIORITY_COLORS[selectedMilestone.priority] }}>{selectedMilestone.priority || "-"}</b></div>
                                        </div>
                                    </div>

                                    <Text type="secondary" style={{ fontSize: 12 }}>Description</Text>
                                    <div style={{ fontSize: 13, marginBottom: 12 }}>{selectedMilestone.description || "-"}</div>

                                    <Text type="secondary" style={{ fontSize: 12 }}>Deliverables</Text>
                                    <div style={{ marginBottom: 4 }}>
                                        {deliverableList.length === 0 && <div style={{ fontSize: 13 }}>-</div>}
                                        {deliverableList.map((d, i) => (
                                            <div key={i} style={{ fontSize: 12.5, color: "#333", marginTop: 4 }}>✓ {d}</div>
                                        ))}
                                    </div>
                                </>
                            )}
                        </Card>
                    </Col>
                </Row>
            </div>
        </Card>
    );
};

export default MilestonesList;